import * as React from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";

import { cn } from "utils/cn";
import { toggleFocusClasses } from "./Checkbox";
import { useFormFieldControl } from "./FormField";
import { useStableId } from "./useStableId";

const trackClasses = cn(
  "inline-flex h-5 w-9 shrink-0 items-center rounded-full border-2 border-transparent bg-input",
  "transition-colors duration-fast ease-standard",
  toggleFocusClasses,
  "disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary"
);

export interface SwitchProps extends React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  /** Text next to the switch; the whole row then toggles it. */
  label?: React.ReactNode;
  description?: React.ReactNode;
}

export const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitive.Root>, SwitchProps>(
  ({ label, description, className, ...rest }, ref) => {
    // Without its own label the switch takes the surrounding FormField's id and aria wiring.
    const fieldProps = useFormFieldControl(rest);
    const id = useStableId("switch", rest.id);

    const thumb = (
      <SwitchPrimitive.Thumb
        className={cn(
          "pointer-events-none block h-4 w-4 rounded-full bg-primary-foreground shadow-xs",
          "transition-transform duration-fast ease-standard data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"
        )}
      />
    );

    if (!label) {
      return (
        <SwitchPrimitive.Root
          ref={ref}
          data-ui="switch"
          className={cn(trackClasses, className)}
          {...fieldProps}
        >
          {thumb}
        </SwitchPrimitive.Root>
      );
    }

    const labelId = `${id}-label`;
    const descriptionId = `${id}-description`;
    return (
      <label
        data-ui="switch"
        htmlFor={id}
        className={cn(
          "flex items-start gap-3",
          rest.disabled ? "cursor-not-allowed" : "cursor-pointer",
          className
        )}
      >
        <SwitchPrimitive.Root
          ref={ref}
          className={trackClasses}
          {...rest}
          id={id}
          aria-labelledby={labelId}
          aria-describedby={description ? descriptionId : rest["aria-describedby"]}
        >
          {thumb}
        </SwitchPrimitive.Root>
        <span className={cn("flex min-w-0 flex-col gap-0.5", rest.disabled && "opacity-60")}>
          <span id={labelId} className="text-sm font-medium leading-5 text-foreground">
            {label}
          </span>
          {description && (
            <span id={descriptionId} className="text-sm leading-5 text-muted-foreground">
              {description}
            </span>
          )}
        </span>
      </label>
    );
  }
);
Switch.displayName = "Switch";
